import React, { useState, useEffect } from 'react';
import { appGetItem, appSetItem } from "@/lib/auth-storage";

// Chevron icons as inline SVG components
const ChevronUpIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

const ChevronDownIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

interface UniversityInfo {
  name: string;
  jupasCode: string;
  programmeDetails: string;
  faculty: string;
  period: string;
  interview: string;
}

interface MoreInformationProps {
  universityInfo: UniversityInfo;
  progUrl?: string;
  jupasUrl?: string;
  firstYearIntake?: string;
  programmeNote?: string;
  subjectMER?: string;
  translate: any;
  stripJSPrefix: (code: string) => string;
}

export const MoreInformation: React.FC<MoreInformationProps> = ({
  universityInfo,
  progUrl,
  jupasUrl,
  firstYearIntake,
  programmeNote,
  subjectMER,
  translate,
  stripJSPrefix
}) => {
  // Expand/collapse state in the in-memory app store.
  const [isExpanded, setIsExpanded] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = appGetItem('collapsible_moreInfo');
      return stored !== null ? stored === 'true' : true;
    }
    return true;
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      appSetItem('collapsible_moreInfo', String(isExpanded));
    }
  }, [isExpanded]);

  return (
    <div className="rounded-lg overflow-hidden bg-amber-50">
      {/* Collapsible Header */}
      <div 
        className="flex items-center justify-between px-3 sm:px-4 py-3 bg-amber-100 cursor-pointer select-none"
        onClick={() => setIsExpanded(prev => !prev)}
      >
        <h4 className="font-semibold text-amber-700 text-xs sm:text-sm">{translate('moreInformation')}</h4>
        <button
          onClick={(e) => { e.stopPropagation(); setIsExpanded(prev => !prev); }}
          className="p-1 rounded-md hover:bg-amber-200 transition-colors duration-200 text-amber-700"
          aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
        >
          {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </button>
      </div>

      {/* Collapsible Content */}
      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
        isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
      }`}>
      <div className="p-3 sm:p-4">
      <table className="w-full text-xs sm:text-sm border border-amber-200 rounded-lg overflow-hidden">
        <tbody>
          {[
            { label: translate('university'), value: universityInfo.name },
            { label: translate('jupasNumber'), value: stripJSPrefix(universityInfo.jupasCode) },
            { label: translate('programme'), value: universityInfo.programmeDetails },
            { label: translate('faculty'), value: universityInfo.faculty },
            ...(progUrl ? [{
              label: translate('progWebsite'),
              value: <a href={progUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline break-all">{progUrl}</a>
            }] : []),
            ...(jupasUrl ? [{
              label: translate('viewOnJupas'),
              value: <a href={jupasUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline break-all">{jupasUrl}</a>
            }] : []),
            { label: translate('period'), value: universityInfo.period },
            { label: translate('interview'), value: universityInfo.interview },
            { label: translate('quota'), value: firstYearIntake || '-' },
            // ...(programmeNote ? [{
            //   label: 'Comments',
            //   value: <div className="text-sm whitespace-pre-wrap">{programmeNote}</div>
            // }] : []),
            ...(subjectMER ? [{
              label: 'Subjects',
              value: subjectMER.replace(/,+\s*$/, '')
            }] : []),
          ].map((row, idx) => (
            <tr key={idx} className={`border-b border-amber-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-amber-50'} hover:bg-amber-100 transition-colors`}>
              <td className="whitespace-nowrap font-semibold py-1.5 sm:py-2 px-2 sm:px-3 align-top bg-amber-100 text-amber-800 w-24 sm:w-32">{row.label}:</td>
              <td className="py-1.5 sm:py-2 px-2 sm:px-3 break-words">{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      </div>
    </div>
  );
};
